import { useEffect, useState } from "react";
import type { WidgetField } from "@/domains/buildings/types/buildingWidget";
import { useFieldEvidence } from "@/hooks/buildings/useFieldEvidence";
import { useSaveFieldValue } from "@/hooks/buildings/useSaveFieldValue";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { BuildingDataWidgetSections } from "@/lib/i18n/types/buildings-sections/building-data-widget";
import { BuildingDataReviewDialog } from "./BuildingDataReviewDialog";
import { isAllowedEnumValue } from "./enumValue";
import { getEnumRule } from "./getEnumRule";
import { stripTrailingUnit } from "./stripTrailingUnit";
import { validateFieldValue } from "./validateFieldValue";

export interface ReviewQueueItem {
  field: WidgetField;
  groupTitle: string;
}

interface BuildingDataReviewQueueProps {
  items: ReviewQueueItem[];
  buildingId: string;
  orgId: string | undefined;
  registryCode: string | null;
  canManage: boolean;
  documentPreviewOpen: boolean;
  onClose: () => void;
  onDocPreview: (filename: string) => void;
  onSelectField: (field: WidgetField) => void;
}

function normalizedReviewValue(field: WidgetField, value: string): string {
  return field.unit ? stripTrailingUnit(value) : value.trim();
}

type ReviewShortcut = "close" | "accept" | "reject" | "next" | "previous";

const REVIEW_SHORTCUTS: Record<string, ReviewShortcut> = {
  escape: "close",
  a: "accept",
  x: "reject",
  s: "next",
  arrowdown: "next",
  arrowup: "previous",
  arrowright: "next",
  arrowleft: "previous",
};

interface ReviewActions {
  close: () => void;
  accept: () => void;
  reject: () => void;
  next: () => void;
  previous: () => void;
}

function runReviewShortcut(action: ReviewShortcut, actions: ReviewActions) {
  switch (action) {
    case "close":
      actions.close();
      break;
    case "accept":
      actions.accept();
      break;
    case "reject":
      actions.reject();
      break;
    case "next":
      actions.next();
      break;
    case "previous":
      actions.previous();
      break;
  }
}

/**
 * Shift counts: Shift+Arrow is how a selection is extended character by
 * character, and the passage and reasoning in the dialog are exactly the text a
 * reviewer copies out. Without it the queue would swallow the keystroke and jump
 * to the next field mid-selection. No shortcut wants a modifier anyway.
 */
function hasKeyboardModifier(event: KeyboardEvent): boolean {
  return event.metaKey || event.ctrlKey || event.altKey || event.shiftKey;
}

function isTextEntryTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

/**
 * An open popover / dropdown owns the keyboard. Radix drives its list with the
 * arrow keys and closes it on Escape, so the queue's window-level shortcuts must
 * stand down: stepping through a source chip's filenames would otherwise advance
 * the queue (or accept/reject the field behind it) and leave the list detached.
 * Focus can sit inside the layer — portalled out of the dialog, hence the popper
 * wrapper — or stay on the trigger that opened it.
 *
 * The trigger case is matched on `aria-haspopup` as well as `aria-expanded`:
 * Radix's Popover / Select / Menu triggers set both, while a plain collapsible
 * sets `aria-expanded` alone. Without the second attribute a focused section
 * header (`Group.tsx`) or a field's expand toggle (`FieldRow.tsx`) would kill
 * every queue shortcut, and the dialog traps no focus to prevent that.
 *
 * Exported for unit testing.
 */
export function isInsideOpenOverlay(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return !!target.closest(
    '[data-radix-popper-content-wrapper],[role="listbox"],[role="menu"],[aria-expanded="true"][aria-haspopup]'
  );
}

/** Exported for unit testing. */
export function shouldIgnoreReviewShortcut(event: KeyboardEvent, disabled: boolean): boolean {
  return (
    disabled ||
    hasKeyboardModifier(event) ||
    isTextEntryTarget(event.target) ||
    isInsideOpenOverlay(event.target)
  );
}

function useReviewShortcuts(actions: ReviewActions, disabled: boolean) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (shouldIgnoreReviewShortcut(event, disabled)) return;
      const action = REVIEW_SHORTCUTS[event.key.toLowerCase()];
      if (!action) return;
      event.preventDefault();
      runReviewShortcut(action, actions);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [actions, disabled]);
}

function currentQueueItem(items: ReviewQueueItem[], index: number): ReviewQueueItem | undefined {
  if (items.length === 0) return undefined;
  return items[index % items.length];
}

/** The rendered position, safe for an empty queue (nothing renders then). */
function currentQueueIndex(items: ReviewQueueItem[], index: number): number {
  return items.length > 0 ? index % items.length : 0;
}

/** The candidate pick is scoped to its field and forgotten when another field is shown. */
export function chosenValue(
  chosen: { fieldId: string; value: string } | null,
  item: ReviewQueueItem | undefined
): string {
  if (!item) return "";
  return chosen && chosen.fieldId === item.field.id ? chosen.value : item.field.value;
}

/**
 * Keep the header position and total on the same stable queue: resolved items
 * advance the position while remaining items shrink.
 */
export function reviewProgress(resolvedCount: number, index: number, remaining: number) {
  return { index: resolvedCount + index, total: resolvedCount + remaining };
}

/** Nothing to review, or no permission to review it, keeps the dialog unmounted. */
function canReview(canManage: boolean, item: ReviewQueueItem | undefined): item is ReviewQueueItem {
  return canManage && !!item;
}

/** Select the resolved field and close only after resolving the queue's last item. */
export function finishReviewAction(
  item: ReviewQueueItem | undefined,
  index: number,
  total: number,
  onSelectField: (field: WidgetField) => void,
  onClose: () => void
) {
  if (!item) return;
  onSelectField(item.field);
  if (index >= total - 1) onClose();
}

interface SaveReviewValueProps {
  item: ReviewQueueItem | undefined;
  mutation: ReturnType<typeof useSaveFieldValue>;
  canManage: boolean;
  value: string | null;
  previousValue: string | null;
  successMessage: string;
  onSuccess: () => void;
}

function saveReviewValue(props: SaveReviewValueProps) {
  if (!props.canManage || !props.item || props.mutation.isPending) return;
  props.mutation.mutate(
    {
      attributeKey: props.item.field.id,
      attributeValue: props.value,
      previousValue: props.previousValue,
      offerUndo: true,
      successMessage: props.successMessage,
    },
    { onSuccess: props.onSuccess }
  );
}

function useReviewQueueState(items: ReviewQueueItem[]) {
  const [index, setIndex] = useState(0);
  const [chosen, setChosen] = useState<{ fieldId: string; value: string } | null>(null);
  const [resolvedFieldIds, setResolvedFieldIds] = useState<Set<string>>(() => new Set());
  const [pendingExtremeValue, setPendingExtremeValue] = useState<string | null>(null);
  const reviewableItems = items.filter((entry) => !resolvedFieldIds.has(entry.field.id));
  const safeIndex = currentQueueIndex(reviewableItems, index);
  const item = currentQueueItem(reviewableItems, index);
  const value = chosenValue(chosen, item);
  const progress = reviewProgress(resolvedFieldIds.size, safeIndex, reviewableItems.length);
  return {
    setIndex,
    setChosen,
    setResolvedFieldIds,
    pendingExtremeValue,
    setPendingExtremeValue,
    reviewableItems,
    safeIndex,
    item,
    value,
    progress,
  };
}

type ReviewQueueState = ReturnType<typeof useReviewQueueState>;

function createReviewQueueActions(
  props: BuildingDataReviewQueueProps,
  state: ReviewQueueState,
  saveMutation: ReturnType<typeof useSaveFieldValue>,
  language: Parameters<typeof getEnumRule>[1],
  tw: BuildingDataWidgetSections
) {
  const move = (direction: 1 | -1) => {
    state.setPendingExtremeValue(null);
    state.setIndex(
      (current) =>
        (current + direction + state.reviewableItems.length) %
        Math.max(state.reviewableItems.length, 1)
    );
  };
  const finishAction = () => {
    const item = state.item;
    if (!item) return;
    state.setResolvedFieldIds((current) => new Set(current).add(item.field.id));
    state.setPendingExtremeValue(null);
    finishReviewAction(item, 0, state.reviewableItems.length, props.onSelectField, props.onClose);
  };
  const save = (value: string | null, previousValue: string | null, successMessage: string) =>
    saveReviewValue({
      item: state.item,
      mutation: saveMutation,
      canManage: props.canManage,
      value,
      previousValue,
      successMessage,
      onSuccess: finishAction,
    });
  const accept = () => {
    if (!state.item) return;
    const normalizedValue = normalizedReviewValue(state.item.field, state.value);
    const enumRule = getEnumRule(state.item.field.validation, language);
    if (enumRule && !isAllowedEnumValue(normalizedValue, enumRule)) {
      props.onSelectField(state.item.field);
      props.onClose();
      return;
    }
    if (!enumRule && !validateFieldValue(normalizedValue, state.item.field.validation).ok) {
      state.setPendingExtremeValue(normalizedValue);
      return;
    }
    save(normalizedValue, "", tw.valueAccepted);
  };
  // SQL NULL records the rejection without leaking an app-private sentinel to
  // reports, exports, LLM context, or direct database readers.
  const reject = () => save(null, "", tw.valueRejected);
  const selectValue = (next: string) => {
    if (!state.item) return;
    state.setChosen({ fieldId: state.item.field.id, value: next });
    state.setPendingExtremeValue(null);
  };
  const editExtremeValue = () => {
    if (!state.item) return;
    props.onSelectField(state.item.field);
    props.onClose();
  };
  const saveExtremeValue = () => {
    if (state.pendingExtremeValue !== null) {
      save(state.pendingExtremeValue, "", tw.valueAccepted);
    }
  };
  return { move, accept, reject, selectValue, editExtremeValue, saveExtremeValue };
}

export function BuildingDataReviewQueue(props: BuildingDataReviewQueueProps) {
  const { t, language } = useLanguage();
  const tw = t.buildings.buildingDataWidget;
  const state = useReviewQueueState(props.items);
  const saveMutation = useSaveFieldValue(props.buildingId, props.orgId);
  const evidence = useFieldEvidence(
    props.buildingId,
    state.item?.field ?? null,
    state.value,
    !!state.item
  );
  const actions = createReviewQueueActions(props, state, saveMutation, language, tw);
  useReviewShortcuts(
    {
      close: props.onClose,
      accept: actions.accept,
      reject: actions.reject,
      next: () => actions.move(1),
      previous: () => actions.move(-1),
    },
    props.documentPreviewOpen || saveMutation.isPending || !canReview(props.canManage, state.item)
  );
  if (!canReview(props.canManage, state.item)) return null;
  return (
    <BuildingDataReviewDialog
      {...props}
      item={state.item}
      value={state.value}
      index={state.progress.index}
      total={state.progress.total}
      evidence={evidence}
      isSaving={saveMutation.isPending}
      tw={tw}
      onPrevious={() => actions.move(-1)}
      onNext={() => actions.move(1)}
      onReject={actions.reject}
      onAccept={actions.accept}
      onSelectValue={actions.selectValue}
      pendingExtremeValue={state.pendingExtremeValue}
      onEditExtremeValue={actions.editExtremeValue}
      onSaveExtremeValue={actions.saveExtremeValue}
    />
  );
}
